import { css } from '@emotion/react';
import type { CSSInterpolation } from '@emotion/serialize';
import _ from 'lodash';
import { useMemo } from 'react';

import IconLock from '~icons/bx/lock';
import IconChevronDown from '~icons/ci/chevron-down';

import { contrastingTextColor, theme } from '@adflow/frontend/ui-theme';

import { HubTooltip } from './HubTooltip';
import { Menu, MenuButton, MenuItem, MenuList } from './MenuButton';
import { StatusDisplay } from './StatusDisplay';
import { Tag } from './Tag';

const styles = {
	icon: css({
		display: 'flex',
		flexDirection: 'row',
		alignItems: 'center',
		alignSelf: 'center',
		justifyContent: 'center',
		marginRight: '-0.25rem',
		marginTop: 1,
		justifySelf: 'center',
	}),

	// make pill take full width
	listItem: css({ display: 'flex', flexDirection: 'row' }),
	listItemPill: (border: boolean) => [
		css({ flexGrow: '1' }),
		border &&
			css({
				borderColor: theme.colors.text.secondary,
				borderWidth: '1px',
			}),
	],
};

export interface StatusOption<T> {
	value: T;
	color: string;
	textColor?: string;
}

export interface StatusMenuProps<T> {
	outerStyle?: CSSInterpolation;
	status: T;
	statusOptions: StatusOption<T>[];
	onStatusChange: (status: T) => void;
	disabled?: boolean;
	/**
	 * Needed to have a correct backgroundColor for archived statuses
	 */
	statusColor?: string;
	manuallyConverted?: boolean;
}

const defaultBackground = theme.colors.background.tag.green;

export function StatusMenu<T extends string>({
	status,
	statusOptions,
	onStatusChange,
	outerStyle,
	disabled,
	statusColor,
	manuallyConverted,
}: StatusMenuProps<T>) {
	const backgroundColor = useMemo(
		() =>
			statusOptions.find((option) => option.value === status)?.color ??
			statusColor ??
			defaultBackground,
		[status, statusOptions, statusColor]
	);

	const handleOnClick = (newStatus: T) => {
		onStatusChange(newStatus);
	};

	const textColor =
		statusOptions.find((so) => !!so.textColor)?.textColor ??
		contrastingTextColor(backgroundColor);

	const statusDisabled =
		disabled || (status === 'converted' && !manuallyConverted);

	return (
		<div css={outerStyle}>
			<Menu
				trigger={
					<MenuButton
						data-testid="status-menu"
						data-disabled={statusDisabled}
						css={[
							{ height: '100%' },
							statusDisabled && { opacity: '0.5', cursor: 'default' },
						]}
					>
						<StatusDisplay
							status={status}
							backgroundColor={backgroundColor}
							textColor={textColor}
							leftIcon={
								status === 'converted' && !manuallyConverted ? (
									<HubTooltip title="This quote was converted on the website and cannot be reversed.">
										<IconLock
											width={20}
											height={20}
											css={[
												styles.icon,
												{ color: textColor, marginRight: '0.2rem' },
											]}
										/>
									</HubTooltip>
								) : null
							}
							rightIcon={
								<IconChevronDown
									width={20}
									height={20}
									css={[
										styles.icon,
										{ color: textColor },
										disabled && { display: 'none' },
									]}
								/>
							}
						/>
					</MenuButton>
				}
			>
				<MenuList
					css={statusDisabled ? { display: 'none' } : null}
					data-testid="status-menu-popover"
				>
					{statusOptions.map((option) => (
						<MenuItem
							key={option.value}
							onSelect={() => handleOnClick(option.value)}
							data-testid={`status-menu-${option.value}`}
							css={styles.listItem}
						>
							<Tag
								text={_.upperFirst(option.value)}
								textStyles={
									option.textColor ? { color: option.textColor } : null
								}
								color={option.color ?? backgroundColor}
								size="clickable"
								css={styles.listItemPill(option.color === '#FFFFFF')}
							/>
						</MenuItem>
					))}
				</MenuList>
			</Menu>
		</div>
	);
}
